import { formatDate } from "date-fns";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS } from "components";
import ENV from "config/Env";
import { usePermanentRealTimeUser, useUploadAttachment } from "hooks";
import { createFormHelper } from "lib/Form";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { CarNoteModalFormValues } from "components";
import type { Car, CarNote, CarNoteAttachment, DealerLocation } from "models";

type CarNoteModalFormProps = {
  car: Car;
  hasErrors: boolean;
  isPending: boolean;
  selectedNote: CarNote | null;
  location?: DealerLocation;
  onClose: () => void;
  onSubmit: (values: CarNoteModalFormValues) => void;
  onUnselectNote: () => void;
  handleOpenDeleteCarNoteModal: () => void;
};

const FormHelper = createFormHelper<CarNoteModalFormValues, CarNoteAttachment>();

export const CarNoteModalForm = ({
  car,
  location,
  hasErrors,
  isPending,
  selectedNote,
  onClose,
  onSubmit,
  onUnselectNote,
  handleOpenDeleteCarNoteModal
}: CarNoteModalFormProps) => {
  const { t } = useTranslation();
  const { watch, handleSubmit } = FormHelper.useFormContext();

  const { user } = usePermanentRealTimeUser();
  const { handleUploadFileAttachment, isUploadingAttachment } = useUploadAttachment("/notes/attachments/upload", ENV.carBaseURL);

  const note = watch("note");
  const isCarNoteEnabled = location?.is_car_note_enabled;

  const handleUpload = async (file: File): Promise<CarNoteAttachment | null> => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const formData = new FormData();

    const date = formatDate(car.created_on, DATE_FORMATS_FNS.yearMonthDateSlash);
    const path = `${location?.id}_${location?.name}/${date}/${car.id}_${car.reg_nr}`;

    formData.append("file", file);
    formData.append("path", path);

    const url = await handleUploadFileAttachment({ formData });
    if (!url) return null;

    return {
      url,
      type: file.type || extension,
      name: file.name,
      created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      username: `${user?.first_name || ""} ${user?.last_name || ""}`,
      user_id: user?.id ?? 0,
      car_note_id: selectedNote?.id ?? 0,
      id: -1,
      updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
    };
  };

  return (
    <>
      <Modal.Header className="header">
        <div className="header-container">
          <Icon className="car" />
          <span className="title">{selectedNote ? t("v8_edit_car_details_note", "Edit car details note") : t("v8_add_car_details_note", "Add car details note")}</span>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="content-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!note && hasErrors}>
              <label>{t("v8_note", "Note")}</label>
              <FormHelper.Field.TextArea
                name="note"
                textAreaProps={{ placeholder: `${t("v8_type_note_here", "Type note here")}...`, rows: 5, disabled: !isCarNoteEnabled }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal" floated="right">
          <GridColumn floated="right">
            <div className="toggles-container">
              <FormHelper.Field.Checkbox
                name="visible_to_mechanic"
                label={t("v8_note_visible_to_mechanic", "Note visible to mechanic")}
                checkboxProps={{ toggle: true }}
                disabled={!isCarNoteEnabled}
              />
              <FormHelper.Field.Checkbox
                name="visible_on_wo"
                label={t("v8_visible_on_wo", "Visible on WO")}
                checkboxProps={{ toggle: true }}
                disabled={!isCarNoteEnabled}
              />
            </div>
          </GridColumn>
        </Grid>

        <FormHelper.Field.Attachments
          name="attachments"
          isUploading={isUploadingAttachment}
          onUpload={handleUpload}
          disabled={!isCarNoteEnabled}
          onPreviewAttachments={attachments => attachments.map(({ url, name, created_on, user_id, username }) => ({ url, name, created_on, user_id, username }))}
        />
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="actions-container">
        <div className="update-car-note-modal-actions">
          <Can I="delete" the="car-notes">
            {selectedNote && (
              <Form.Button onClick={handleOpenDeleteCarNoteModal} color="red">
                {t("v8_delete", "Delete")}
                <Icon className="trash" />
              </Form.Button>
            )}
          </Can>
          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={selectedNote && selectedNote.id > 0 ? onUnselectNote : onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            {isCarNoteEnabled && (
              <Can I={["create", "update"]} the="car-notes">
                <Form.Button
                  type="submit"
                  color="green"
                  onClick={handleSubmit(d => onSubmit(d))}
                  loading={isPending}
                  disabled={isPending || isUploadingAttachment || !isCarNoteEnabled}
                >
                  {!selectedNote ? t("v8_save", "Save") : t("v8_update", "Update")}
                  <Icon className="check" />
                </Form.Button>
              </Can>
            )}
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
